import { isSupabaseConfigured, supabase } from '../../lib/supabaseClient';

const VALID_ROLES = ['admin', 'editor', 'viewer'];

function normalizeRole(role) {
  return VALID_ROLES.includes(role) ? role : 'viewer';
}

function extractErrorMessage(error) {
  if (!error) {
    return 'Authentication failed.';
  }

  if (typeof error === 'string') {
    return error;
  }

  return error.message || 'Authentication failed.';
}

function toAdminSession({ session, user, role }) {
  return {
    id: user.id,
    email: user.email || '',
    role: normalizeRole(role),
    signedInAt: session?.created_at || new Date().toISOString()
  };
}

function ensureSupabaseConfigured() {
  if (!isSupabaseConfigured) {
    throw new Error(
      'Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_PUBLISHABLE_KEY (or ANON key).'
    );
  }
}

export async function fetchAdminRole(authUserId) {
  ensureSupabaseConfigured();

  if (!authUserId) {
    return null;
  }

  const { data, error } = await supabase
    .from('admin_users')
    .select('role, is_active')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (error) {
    throw new Error(extractErrorMessage(error));
  }

  if (!data || data.is_active === false) {
    return null;
  }

  return normalizeRole(data.role);
}

export async function loginWithSupabaseCredentials({ email, password }) {
  ensureSupabaseConfigured();

  const normalizedEmail = (email || '').trim().toLowerCase();
  const normalizedPassword = (password || '').trim();

  if (!normalizedEmail) {
    throw new Error('Email is required.');
  }
  if (normalizedPassword.length < 6) {
    throw new Error('Password must be at least 6 characters.');
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email: normalizedEmail,
    password: normalizedPassword
  });

  if (error) {
    throw new Error(extractErrorMessage(error));
  }

  const session = data?.session;
  const user = data?.user;

  if (!session || !user) {
    throw new Error('Sign in did not return a valid session.');
  }

  const role = await fetchAdminRole(user.id);

  if (!role) {
    await supabase.auth.signOut();
    throw new Error('Your account is not authorized for the admin panel.');
  }

  return toAdminSession({ session, user, role });
}

export async function getSupabaseAdminSession() {
  ensureSupabaseConfigured();

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw new Error(extractErrorMessage(error));
  }

  const session = data?.session;
  const user = session?.user;

  if (!session || !user) {
    return null;
  }

  const role = await fetchAdminRole(user.id);
  if (!role) {
    return null;
  }

  return toAdminSession({ session, user, role });
}

export async function logoutFromSupabase() {
  if (!isSupabaseConfigured) {
    return;
  }

  const { error } = await supabase.auth.signOut();
  if (error) {
    throw new Error(extractErrorMessage(error));
  }
}

export function subscribeToAuthChanges(onChange) {
  if (!isSupabaseConfigured) {
    return () => {};
  }

  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    onChange(session);
  });

  return () => {
    data?.subscription?.unsubscribe();
  };
}

export function canAccessRole(session, allowedRoles) {
  if (!session) {
    return false;
  }

  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
    return true;
  }

  return allowedRoles.includes(normalizeRole(session.role));
}

export const adminRoles = VALID_ROLES;
